import { Lang, detectLangFromPath } from "./types";

interface Routes {
  landing: string;
  personal: string;
  articles: string;
  articleDownloadTroubles: string;
  articlePrintGuide: string;
  articleDiyGuide: string;
  articleLookGood: string;
  editor: string;
  privacy: string;
  tos: string;
  tosRefunds: string;
  contact: string;
  appInfo: string;
  proEarlyAccess: string;
  "404": string;
}

export type Route = keyof Routes;

export const siteUrl = "https://passportphotolab.com";

// Note: articles should be handled with graphql. E.g. hreflang is done in article specific files.

export const urls: { [key in Lang]: Routes } = {
  en: {
    landing: "/",
    personal: "/personal/",
    articles: "/articles/",
    articleDownloadTroubles: "/articles/where-are-downloaded-photos-saved/",
    articlePrintGuide: "/articles/print-photos-correct-size/",
    articleDiyGuide: "/articles/passport-photo-diy-home-guide/",
    articleLookGood: "/articles/how-to-take-a-good-passport-photo-pose-tips/",
    editor: "/editor/",
    privacy: "/privacy-policy/",
    tos: "/terms-of-use/",
    tosRefunds: "/terms-of-use/#refunds",
    contact: "/contact-us/",
    appInfo: "/info/",
    proEarlyAccess: "/pro-early-access/",
    "404": "/404/",
  },
  fi: {
    landing: "/fi/",
    personal: "/fi/personal/",
    articles: "/fi/artikkelit/",
    articleDownloadTroubles: "/fi/artikkelit/mista-loydan-lataamani-kuvat/",
    articlePrintGuide: "/fi/artikkelit/kuvien-tulostus-oikeassa-koossa/",
    articleDiyGuide: "/fi/artikkelit/ota-passikuva-itse/",
    articleLookGood: "/fi/artikkelit/kuinka-nayttaa-hyvalta-passikuvassa/",
    editor: "/fi/editori/",
    privacy: "/fi/tietosuojaseloste/",
    tos: "/fi/kayttoehdot/",
    tosRefunds: "/fi/kayttoehdot/#refunds",
    contact: "/fi/ota-yhteytta/",
    appInfo: "/info/",
    proEarlyAccess: "/fi/pro-early-access/",
    "404": "/404/",
  },
} as const;

export function getRouteLangVersions(
  key: Route,
): { lang: Lang; url: string }[] {
  const results: { lang: Lang; url: string }[] = [];
  // 'en' forced as the first item, to make sure it's the one selected if multiple language versions have the english url (== not translated)
  for (let [lang, langUrls] of [
    ["en", urls.en] as [Lang, Routes],
    ...Object.entries(urls),
  ]) {
    const url = `${siteUrl}${langUrls[key]}`;
    if (results.findIndex((r) => r.url === url) < 0) {
      results.push({ lang: lang as Lang, url });
    }
  }
  return results;
}

/**
 * Whether the given pathname belongs to the editor app (dark theme).
 * Static/marketing pages use the light theme; the editor uses the dark theme.
 */
export function isEditorPath(pathname: string): boolean {
  return Object.values(urls).some((langUrls) =>
    pathname.startsWith(langUrls.editor),
  );
}

export function getArticleFullUrl(slug: string): string {
  return `${siteUrl}${slug}`;
}

export function getAboutUsLink(currentPath: string, lang: Lang): string {
  const isPersonalPage = currentPath.includes(urls[lang].personal);
  const baseUrl = isPersonalPage ? urls[lang].personal : urls[lang].landing;
  return `${baseUrl}#about`;
}

export function getPricingLink(currentPath: string, lang: Lang): string {
  const isPersonalPage = currentPath.includes(urls[lang].personal);
  const baseUrl = isPersonalPage ? urls[lang].personal : urls[lang].landing;
  return `${baseUrl}#pricing`;
}

export function getStandardsLink(currentPath: string, lang: Lang): string {
  const isPersonalPage = currentPath.includes(urls[lang].personal);
  const baseUrl = isPersonalPage ? urls[lang].personal : urls[lang].landing;
  return `${baseUrl}#standards`;
}

export function getCurrentRouteForLang(
  currentPath: string,
  targetLang: Lang,
): string {
  // Optimization: If target language is same as current, stay on current path
  // This preserves dynamic paths like /articles/some-slug/ when clicking the current language
  const hashIndex = currentPath.indexOf("#");
  const pathWithoutHash =
    hashIndex >= 0 ? currentPath.substring(0, hashIndex) : currentPath;

  const currentLang = detectLangFromPath(pathWithoutHash);

  if (targetLang === currentLang) {
    return currentPath;
  }

  // 1. Parse path, query, hash
  let hash = "";
  let query = "";
  let path = currentPath;

  if (hashIndex >= 0) {
    hash = currentPath.substring(hashIndex);
    path = currentPath.substring(0, hashIndex);
  }

  const queryIndex = path.indexOf("?");
  if (queryIndex >= 0) {
    query = path.substring(queryIndex);
    path = path.substring(0, queryIndex);
  }

  // 2. Prepare routes for matching
  const normalize = (p: string) => (p.endsWith("/") ? p : p + "/");
  const normalizedPath = normalize(path);

  const routes = Object.entries(urls[currentLang]).map(([key, url]) => {
    let urlPath = String(url);
    const hIdx = urlPath.indexOf("#");
    if (hIdx >= 0) urlPath = urlPath.substring(0, hIdx);
    const qIdx = urlPath.indexOf("?");
    if (qIdx >= 0) urlPath = urlPath.substring(0, qIdx);
    return {
      key: key as Route,
      urlPath: normalize(urlPath),
    };
  });

  // 3. Loop and strip
  let candidate = normalizedPath;
  while (true) {
    const match = routes.find((r) => r.urlPath === candidate);
    if (match) {
      let targetRouteUrl = urls[targetLang][match.key];

      // Strip hash/query from target url to avoid duplication
      const tHIdx = targetRouteUrl.indexOf("#");
      if (tHIdx >= 0) targetRouteUrl = targetRouteUrl.substring(0, tHIdx);
      const tQIdx = targetRouteUrl.indexOf("?");
      if (tQIdx >= 0) targetRouteUrl = targetRouteUrl.substring(0, tQIdx);

      // If we matched the original path, keep query and hash
      if (candidate === normalizedPath) {
        return targetRouteUrl + query + hash;
      } else {
        // We stripped segments, so we discard query and hash
        return targetRouteUrl;
      }
    }

    if (candidate === "/" || candidate === "") {
      break;
    }

    // Strip last segment
    const noTrailing = candidate.endsWith("/")
      ? candidate.slice(0, -1)
      : candidate;
    const lastSlash = noTrailing.lastIndexOf("/");
    if (lastSlash < 0) {
      candidate = "/";
    } else {
      candidate = noTrailing.substring(0, lastSlash) + "/";
    }
  }

  // 4. Fallback
  return urls[targetLang].landing;
}
